import { UNASSIGNED_LABEL } from "@/constants/adminCollections";
import { PRODUCT_IMAGE_FALLBACK } from "@/constants/campaignBuilder";
import type { AdminCollectionProduct } from "@/interfaces/adminCollections";
import { formatAssignmentChip, formatUsd } from "@/utils/adminCollections-helper";

interface CollectionProductCardProps {
  product: AdminCollectionProduct;
  isCustom: boolean;
  onAssign?: (product: AdminCollectionProduct) => void;
}

const CARD_CLASS = "flex flex-col overflow-hidden rounded-[10px] border border-smoke bg-white";

/**
 * Read-only product tile. In the Custom Collection the whole tile is the control, as in the
 * prototype: clicking it (or Enter/Space when focused) opens the assign modal, and a chip shows
 * the current organization or "Unassigned". There is no separate Assign / Change button.
 */
export default function CollectionProductCard({
  product,
  isCustom,
  onAssign,
}: CollectionProductCardProps) {
  const imageSrc = product.imageUrl || PRODUCT_IMAGE_FALLBACK;
  const assignment = isCustom ? product.assignment : null;

  const body = (
    <>
      <div className="flex aspect-square w-full items-center justify-center overflow-hidden bg-white p-[22px]">
        <img
          src={imageSrc}
          alt=""
          loading="lazy"
          onError={(event) => {
            const img = event.currentTarget;
            if (img.src !== PRODUCT_IMAGE_FALLBACK) img.src = PRODUCT_IMAGE_FALLBACK;
          }}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-[18px]">
        <span className="font-bold font-lato text-[10px] text-dark-dust uppercase tracking-[1.5px]">
          {product.category}
        </span>
        <span className="font-bold font-lato text-[15px] text-rodeo-black leading-[1.3]">
          {product.name}
        </span>
        {product.price !== null && (
          <span className="font-lato text-graphite text-sm">
            {formatUsd(product.price, { cents: true })}
          </span>
        )}
        {isCustom && (
          <span
            className={`mt-2 self-start rounded-full px-2.5 py-1 font-lato text-xs ${
              assignment ? "bg-light-dust text-rodeo-black" : "bg-smoke/40 text-graphite"
            }`}
          >
            {assignment ? formatAssignmentChip(assignment) : UNASSIGNED_LABEL}
          </span>
        )}
      </div>
    </>
  );

  if (!isCustom || !onAssign) return <div className={CARD_CLASS}>{body}</div>;

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      onClick={() => onAssign(product)}
      className={`${CARD_CLASS} cursor-pointer p-0 text-left transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2`}
    >
      {body}
    </button>
  );
}
